import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Wifi, Trash2, AlertCircle, Printer, Utensils, FileText, Zap, RefreshCw, Radio } from "lucide-react";
import { invalidatePrintersCache } from "@/lib/usePrecontoPrinter";
import { useStore } from "@/lib/storeContext";
import { connectQz, qzListSystemPrinters, lastQzError } from "@/lib/qz";
import { pingLanHost, isNativePrint, getLanInfo, fetchPrinterPage } from "@/lib/printTransport";
import PrecontoLinesEditor, { StyledReceiptLines } from "@/components/PrecontoLinesEditor";
import PrintLayoutEditor from "@/components/PrintLayoutEditor";

const strToLines = (s) =>
  String(s || "")
    .split("\n")
    .filter((t, i, arr) => t.trim() !== "" || i < arr.length - 1)
    .map((t) => ({ text: t, size: "normal", bold: false }));
const linesToStr = (lines) => (Array.isArray(lines) ? lines : []).map((l) => l.text || "").join("\n");

// Modelli supportati per ruolo (hardware certificato VELTA)
const MODELS_BY_ROLE = {
  fiscal: [
    { v: "epson_fp81ii", label: "Epson FP81II", proto: "ePOS-Print XML · LAN", port: 80 },
    { v: "rch_printf3", label: "RCH Print!F3 RT", proto: "Protocollo RCH nativo · LAN", port: 23 },
    { v: "simulator", label: "Simulatore (sviluppo)", proto: "Simulazione", port: 9100 },
  ],
  preconto: [
    { v: "axon_pos80k", label: "AXON MICRELEC POS80K", proto: "ESC/POS · LAN", port: 9100 },
    { v: "simulator", label: "Simulatore (sviluppo)", proto: "Simulazione", port: 9100 },
  ],
  comanda: [
    { v: "axon_pos80k", label: "AXON MICRELEC POS80K", proto: "ESC/POS · LAN", port: 9100 },
    { v: "epson_tm_m30ii", label: "Epson TM-M30II-SL", proto: "ESC/POS · ePOS SDK · LAN", port: 9100 },
    { v: "pluricode_203", label: "Pluricode 203", proto: "ESC/POS standard · LAN", port: 9100 },
    { v: "simulator", label: "Simulatore (sviluppo)", proto: "Simulazione", port: 9100 },
  ],
};
const LEGACY_LABELS = {
  epson: { label: "Epson (legacy)", proto: "ESC/POS" },
  rch: { label: "RCH (legacy)", proto: "ESC/POS" },
  custom: { label: "Custom (legacy)", proto: "ESC/POS" },
  ditron: { label: "Ditron (legacy)", proto: "ESC/POS" },
};
const modelInfo = (role, v) =>
  MODELS_BY_ROLE[role]?.find((m) => m.v === v) || LEGACY_LABELS[v] || { label: v || "—", proto: "" };

function defaultPortFor(role, model) {
  const m = MODELS_BY_ROLE[role]?.find((x) => x.v === model);
  if (m && m.port) return m.port;
  if (role === "fiscal") return 80;
  return 9100;
}

export default function Printers() {
  const { stores, storeId } = useStore();
  const storeList = Array.isArray(stores) ? stores : [];
  const multi = storeList.length > 1;
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [brand, setBrand] = useState({ header_lines: [], footer_lines: [] });
  const [brandSaving, setBrandSaving] = useState(false);
  const [qzState, setQzState] = useState("checking"); // checking | connected | offline
  const [qzPrinters, setQzPrinters] = useState([]);
  const [connState, setConnState] = useState({}); // printerId -> ok | fail | testing | unknown
  const [nativeLan, setNativeLan] = useState(false);
  const [lanInfo, setLanInfo] = useState(null);
  const [diagLog, setDiagLog] = useState("");
  const [diagBusy, setDiagBusy] = useState(false);
  const itemsRef = useRef([]);
  itemsRef.current = items;

  const [qzHint, setQzHint] = useState("");
  const checkQz = () => {
    setQzState("checking");
    setQzHint("");
    isNativePrint().then((native) => {
      setNativeLan(native);
      if (native) {
        setQzState("connected");
        getLanInfo().then(setLanInfo).catch(() => {});
        return;
      }
      connectQz(25000).then(async (ok) => {
        setQzState(ok ? "connected" : "offline");
        setQzHint(ok ? "" : lastQzError || "");
        if (ok) setQzPrinters(await qzListSystemPrinters());
      });
    });
  };
  const runDiag = async () => {
    setDiagBusy(true);
    const lines = ["Avvio diagnosi…"];
    const show = (line) => {
      lines.push(line);
      setDiagLog(lines.join("\n"));
    };
    setDiagLog(lines.join("\n"));
    try {
      const { Capacitor } = await import("@capacitor/core");
      show(`native=${!!Capacitor.isNativePlatform?.()} plugin=${!!Capacitor.isPluginAvailable?.("TcpPrint")}`);
      const lan = await Promise.race([
        getLanInfo(),
        new Promise((resolve) => setTimeout(() => resolve(null), 4000)),
      ]);
      show(`lan ip=${lan?.ip || "—"} ssid=${lan?.ssid || "—"}`);
      show("Prova pagina verso Epson (senza plugin)…");
      try {
        const ctrl = new AbortController();
        const kill = setTimeout(() => ctrl.abort(), 4000);
        await fetch("http://192.168.1.23/", { mode: "no-cors", cache: "no-store", signal: ctrl.signal });
        clearTimeout(kill);
        show("FETCH Epson: la pagina raggiunge 192.168.1.23");
      } catch (e) {
        show(`FETCH Epson FAIL: ${e?.name || ""} ${e?.message || e}`);
      }
      show("Lettura CORS /www/ …");
      try {
        const ctrl = new AbortController();
        const kill = setTimeout(() => ctrl.abort(), 4000);
        const res = await fetch("http://192.168.1.23/www/", { cache: "no-store", signal: ctrl.signal });
        const text = await res.text();
        clearTimeout(kill);
        show(`CORS status=${res.status} bytes=${text.length}`);
      } catch (e) {
        show(`CORS: ${e?.name || ""} ${e?.message || e}`);
      }
      show("--- lettura diretta ---");
      const page = await fetchPrinterPage({ host: "192.168.1.23", port: 80, path: "/www/", method: "GET", timeoutMs: 4000 });
      const snippet = String(page?.response || page?.error || "").replace(/\s+/g, " ").slice(0, 160);
      show(page?.ok ? `PAGE OK ${snippet}` : `PAGE FAIL ${page?.error || snippet}`);
      show("--- preconto 192.168.1.188:9100 ---");
      const pre = await pingLanHost({ host: "192.168.1.188", port: 9100, timeoutMs: 3000 });
      show(pre.ok ? "PRECONTO OK" : `PRECONTO FAIL ${pre.detail || pre.reason || ""}`);
      show("Fine.");
    } catch (e) {
      show(String(e?.message || e));
    }
    setDiagBusy(false);
  };
  useEffect(() => { checkQz(); }, []);

  const load = () => api.get("/printers").then((r) => setItems(Array.isArray(r.data) ? r.data : []));
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/tenant/me").then((r) => {
      const t = r.data || {};
      setBrand({
        header_lines: Array.isArray(t.preconto_header_lines) && t.preconto_header_lines.length ? t.preconto_header_lines : strToLines(t.preconto_header),
        footer_lines: Array.isArray(t.preconto_footer_lines) && t.preconto_footer_lines.length ? t.preconto_footer_lines : strToLines(t.preconto_footer),
        print_layout: t.print_layout || {},
      });
    }).catch(() => {});
  }, []);

  // ---- Test connessione (via QZ Tray: socket verso IP:porta) ----
  const testPrinter = async (p) => {
    setConnState((s) => ({ ...s, [p.id]: "testing" }));
    let next = "fail";
    const watch = setTimeout(() => {
      setConnState((s) => (s[p.id] === "testing" ? { ...s, [p.id]: "fail" } : s));
    }, 14000);
    try {
      if (p.model === "simulator") {
        next = "ok";
        return;
      }
      if (!p.ip) { toast.error(`${p.name}: manca l'IP`); return; }
      const epos = p.model === "epson_fp81ii" || p.model === "epson";
      const saved = parseInt(p.port, 10);
      const tryPorts = epos
        ? [...new Set([saved, 9100, 80].filter((n) => Number.isFinite(n) && n > 0))]
        : [Number.isFinite(saved) && saved > 0 ? saved : 9100];
      let r = { ok: false, reason: "send_failed", detail: "nessuna porta" };
      let port = tryPorts[0];
      for (const prt of tryPorts) {
        port = prt;
        r = await pingLanHost({ host: p.ip, port: prt, timeoutMs: 4500 });
        if (r.ok) break;
      }
      if (!r.ok) {
        const why = r.reason === "qz_offline"
          ? (qzHint || "Dal PC serve QZ Tray (Allow). Sul tablet: stessa Wi‑Fi della stampante, niente QZ.")
          : (r.detail || r.reason || "nessuna risposta");
        toast.error(`${p.name}: ${p.ip}:${port} — ${why}. Stessa Wi‑Fi della stampante, non 4G.`);
        next = r.reason === "qz_offline" ? "unknown" : "fail";
      } else {
        next = "ok";
      }
    } catch (e) {
      toast.error(`${p.name}: ${String(e?.message || e)}`);
      next = "fail";
    } finally {
      clearTimeout(watch);
      setConnState((s) => ({ ...s, [p.id]: next }));
    }
  };

  const saveBrand = async () => {
    setBrandSaving(true);
    try {
      await api.patch("/tenant/branding", {
        preconto_header_lines: brand.header_lines,
        preconto_footer_lines: brand.footer_lines,
        preconto_header: linesToStr(brand.header_lines),
        preconto_footer: linesToStr(brand.footer_lines),
        print_layout: brand.print_layout || {},
      });
      toast.success("Impostazioni di stampa salvate");
    } catch (e) {
      toast.error(formatApiError(e, "Errore salvataggio (solo il titolare può modificare)"));
    } finally { setBrandSaving(false); }
  };

  const save = async (p) => {
    if (!p.name || !p.name.trim()) {
      toast.error("Inserisci un nome per la postazione");
      return;
    }
    if (p.model === "simulator") {
      toast.error("Scegli un modello reale (Epson FP81 / AXON), non Simulatore. Il simulatore non stampa carta e non usa A4.");
      return;
    }
    const sid = p.store_id || "";
    const sameScope = (x) => (x.store_id || "") === sid;
    if (p.role === "fiscal" && !p.id) {
      const already = items.find((x) => x.role === "fiscal" && sameScope(x));
      if (already) {
        toast.error(`Su questa sede c'è già la fiscale «${already.name}».`);
        return;
      }
    }
    if (p.role === "preconto" && !p.id) {
      const already = items.find((x) => x.role === "preconto" && sameScope(x));
      if (already) {
        toast.error(`Su questa sede c'è già il preconto «${already.name}».`);
        return;
      }
    }
    try {
      const portN = parseInt(p.port, 10);
      const port = Number.isFinite(portN) && portN > 0 ? portN : defaultPortFor(p.role, p.model);
      const payload = { ...p, port };
      if (p.id) await api.patch(`/printers/${p.id}`, payload);
      else await api.post("/printers", payload);
      toast.success(p.role === "fiscal" ? "Stampante fiscale salvata" : p.role === "preconto" ? "Stampante Preconto salvata" : `Postazione "${p.name}" salvata`);
      invalidatePrintersCache();
      setEditing(null); load();
    } catch (e) {
      toast.error(formatApiError(e, "Errore salvataggio"));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare stampante?")) return;
    await api.delete(`/printers/${id}`);
    invalidatePrintersCache();
    load();
  };

  const visibleItems = storeId
    ? items.filter((p) => !p.store_id || p.store_id === storeId)
    : items;
  const hasFiscal = visibleItems.some((p) => p.role === "fiscal");
  const hasPrecontoPrinter = visibleItems.some((p) => p.role === "preconto");
  const comandas = (Array.isArray(visibleItems) ? visibleItems : []).filter((p) => p.role === "comanda");
  const storeNameOf = (sid) => storeList.find((s) => s.id === sid)?.name || null;
  const defaultStoreId = storeId || "";

  const cardProps = { cats, connState, onTest: testPrinter, qzState, storeNameOf, multi };

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Hardware</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Stampanti & POS</h1>
          <p className="text-velta-mute mt-2">
            Fiscale, preconto e comande sono opzionali. Con più sedi: scegli la sede in alto e collega le stampanti solo dove serve; una sede può restare senza cassa.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="printer-new-comanda-btn"
            onClick={() => setEditing({ name: "", ip: "192.168.1.110", port: 9100, model: "axon_pos80k", role: "comanda", categories: [], station: "kitchen", store_id: defaultStoreId || null })}
            className="h-12 px-5 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center gap-2 btn-tactile"
          >
            <Utensils className="w-4 h-4" /> Nuova Comanda
          </button>
          <button
            data-testid="printer-new-preconto-btn"
            onClick={() => {
              setEditing({ name: "Preconto", ip: "192.168.1.101", port: 9100, model: "axon_pos80k", role: "preconto", gestionale_enabled: false, store_id: defaultStoreId || null });
            }}
            className="h-12 px-5 bg-white border border-[#B45309] text-[#B45309] hover:bg-[#B45309] hover:text-white rounded-md font-medium flex items-center gap-2 btn-tactile"
            title="Stampante preconto (non fiscale). Non obbligatoria."
          >
            <FileText className="w-4 h-4" /> Aggiungi Preconto
          </button>
          <button
            data-testid="printer-new-fiscal-btn"
            onClick={() => {
              setEditing({ name: "Cassa Fiscale", ip: "192.168.1.23", port: 9100, model: "epson_fp81ii", role: "fiscal", fiscal_serial: "", store_id: defaultStoreId || null });
            }}
            className="h-12 px-5 bg-white border border-velta-ink text-velta-ink hover:bg-velta-ink hover:text-white rounded-md font-medium flex items-center gap-2 btn-tactile"
            title="Stampante fiscale RT. Non obbligatoria se la sede non ha cassa."
          >
            <Printer className="w-4 h-4" /> Aggiungi Fiscale
          </button>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6 flex items-start gap-3 text-sm">
        <AlertCircle className="w-5 h-5 text-yellow-700 shrink-0 mt-0.5" />
        <div className="text-yellow-900">
          <strong>Fiscale (RT)</strong> = opzionale, al massimo una per sede (Epson FP81II o RCH Print!F3 RT).
          <strong> Preconto</strong> = opzionale, una per sede. <strong> Comande</strong> = quante servono per sede. Una sede senza cassa può non avere stampanti.
        </div>
      </div>

      {/* STATO QZ TRAY */}
      <div
        data-testid="qz-status-banner"
        className={`border rounded-md p-4 mb-8 flex items-center gap-3 text-sm ${
          qzState === "connected"
            ? "bg-[#F0FDF4] border-[#2F9E77]/40 text-[#166534]"
            : qzState === "offline"
            ? "bg-[#FFFBEB] border-[#F59E0B]/40 text-[#92400E]"
            : "bg-velta-bg border-velta-border text-velta-mute"
        }`}
      >
        <Zap className={`w-5 h-5 shrink-0 ${qzState === "connected" ? "text-[#2F9E77]" : qzState === "offline" ? "text-[#F59E0B]" : "text-velta-subtle"}`} />
        <div className="flex-1">
          {nativeLan && (
            <>
              <strong>Tablet: stampa diretta in LAN</strong> — Wi‑Fi o cavo, stessa rete della stampante, IP fisso. Non serve internet né QZ Tray.
              {lanInfo && (
                <span className="block text-xs mt-1 font-mono">
                  Questo tablet: {lanInfo.ssid ? `Wi‑Fi «${lanInfo.ssid}» · ` : ""}IP {lanInfo.ip || lanInfo.ips || "—"}.
                  Il PC cassa è su «Velta Sistemi» · 192.168.1.21. Le stampanti sono 192.168.1.23 e 192.168.1.188.
                  {String(lanInfo.ip || "").startsWith("192.168.1.")
                    ? " Stessa rete /24: ok."
                    : " Se l’IP non inizia con 192.168.1. il tablet NON è sulla Wi‑Fi del negozio (dati mobili o altra rete)."}
                </span>
              )}
            </>
          )}
          {!nativeLan && qzState === "checking" && "Verifica QZ Tray… se compare una finestra, clicca Allow (fino a 25 secondi)."}
          {!nativeLan && qzState === "connected" && (
            <>
              <strong>QZ Tray connesso</strong> — stampa diretta sul PC verso IP:porta in LAN
              {qzPrinters.length > 0 && <span className="block text-xs mt-0.5 opacity-80">Stampanti di sistema rilevate: {qzPrinters.join(", ")}</span>}
            </>
          )}
          {!nativeLan && qzState === "offline" && (
            <>
              <strong>QZ Tray non collegato</strong>
              {qzHint ? <span className="block text-xs mt-1">{qzHint}</span> : null}
              <span className="block mt-1">
                In Chrome, nella barra indirizzi, consenti <strong>accesso alla rete locale</strong> / dispositivi locali
                (icona a forma di PC o lucchetto → Impostazioni sito). Poi Riprova: deve comparire Allow di QZ.
              </span>
            </>
          )}
        </div>
        <button
          data-testid="qz-retry-btn"
          onClick={checkQz}
          className="h-9 px-4 bg-white border border-current rounded-md text-xs font-medium flex items-center gap-1.5 btn-tactile shrink-0"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${qzState === "checking" ? "animate-spin" : ""}`} /> Riprova
        </button>
      </div>

      {nativeLan && (
        <div className="bg-white border border-velta-border rounded-md p-4 mb-8" data-testid="lan-diag-panel">
          <button
            type="button"
            onClick={runDiag}
            disabled={diagBusy}
            className="h-11 px-4 bg-[#1E2D5B] text-white rounded-md text-sm font-medium btn-tactile disabled:opacity-50"
          >
            {diagBusy ? "Diagnosi…" : "Diagnosi LAN (log)"}
          </button>
          <p className="text-xs text-velta-mute mt-2">Fai uno screenshot del testo qui sotto e invialo: dice se il plugin TCP vede Wi‑Fi, 4G, Epson e AXON.</p>
          {diagLog ? (
            <pre className="mt-3 text-[11px] font-mono whitespace-pre-wrap bg-[#F4F4F5] border border-velta-border rounded-md p-3 max-h-64 overflow-auto">{diagLog}</pre>
          ) : null}
        </div>
      )}

      {/* FISCALE */}
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-3 flex items-center gap-2">
          <Printer className="w-3.5 h-3.5" /> Stampante Fiscale (RT) — Epson FP81II o RCH Print!F3 RT
        </div>
        {(Array.isArray(visibleItems) ? visibleItems : []).filter((p) => p.role === "fiscal").length === 0 ? (
          <div className="bg-velta-bg border border-dashed border-velta-border rounded-md p-6 text-center text-sm text-velta-mute">
            Nessuna fiscale su questa vista. Non è obbligatoria: una sede può restare senza cassa.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {(Array.isArray(visibleItems) ? visibleItems : []).filter((p) => p.role === "fiscal").map((p) => (
              <PrinterCard key={p.id} p={p} onEdit={() => setEditing(p)} onDelete={() => del(p.id)} {...cardProps} />
            ))}
          </div>
        )}
      </div>

      {/* PRECONTO */}
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-3 flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" /> Stampante Preconto (non fiscale) — AXON MICRELEC POS80K
        </div>
        {(Array.isArray(visibleItems) ? visibleItems : []).filter((p) => p.role === "preconto").length === 0 ? (
          <div className="bg-velta-bg border border-dashed border-velta-border rounded-md p-6 text-center text-sm text-velta-mute">
            Nessuna stampante Preconto configurata. Aggiungila per attivare il pulsante <strong>Preconto</strong> in tutte le casse.
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {(Array.isArray(visibleItems) ? visibleItems : []).filter((p) => p.role === "preconto").map((p) => (
                <PrinterCard key={p.id} p={p} onEdit={() => setEditing(p)} onDelete={() => del(p.id)} {...cardProps} />
              ))}
            </div>
            <div className="mt-4 bg-white border border-velta-border rounded-md p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Intestazione documento Preconto</div>
              <p className="text-xs text-velta-mute mb-4">
                Righe libere con dimensione carattere (S/M/G) e grassetto. Valgono per il <strong>Preconto</strong> e per il <strong>Movimento Gestionale</strong>, su ogni attività e piano. <strong>Se compilata, sostituisce completamente i dati aziendali standard</strong> (nessuna doppia intestazione). Se vuota, viene stampata l&apos;intestazione standard.
              </p>
              <div className="grid lg:grid-cols-3 gap-5">
                <PrecontoLinesEditor
                  label="Intestazione (in alto)"
                  lines={brand.header_lines}
                  onChange={(lines) => setBrand({ ...brand, header_lines: lines })}
                  testid="preconto-hdr"
                />
                <PrecontoLinesEditor
                  label="Piè di pagina (in basso)"
                  lines={brand.footer_lines}
                  onChange={(lines) => setBrand({ ...brand, footer_lines: lines })}
                  testid="preconto-ftr"
                />
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Anteprima</label>
                  <div data-testid="preconto-lines-preview" className="bg-velta-bg border border-dashed border-velta-border rounded-md p-4 font-mono">
                    <StyledReceiptLines lines={brand.header_lines} />
                    <div className="text-center text-[10px] text-velta-subtle my-2">— prodotti e totale —</div>
                    <div className="text-center text-[10px] font-bold">PRECONTO</div>
                    <div className="text-center text-[9px] text-velta-mute mb-1">Documento non fiscale</div>
                    <StyledReceiptLines lines={brand.footer_lines} />
                  </div>
                </div>
              </div>
              <button
                data-testid="preconto-header-save"
                onClick={saveBrand}
                disabled={brandSaving}
                className="mt-4 h-11 px-6 bg-velta-ink text-white rounded-md text-sm font-medium btn-tactile disabled:opacity-40"
              >
                {brandSaving ? "Salvataggio…" : "Salva intestazione"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* LAYOUT DI STAMPA */}
      <div className="mb-8 bg-white border border-velta-border rounded-md p-5" data-testid="print-layout-card">
        <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Layout di stampa — posizione e grandezza</div>
        <p className="text-xs text-velta-mute mb-4">
          Scegli <strong>allineamento</strong> (sinistra / centro / destra) e <strong>dimensione carattere</strong> (S/M/G) di Data, Articoli e Cassiere/Operatore. Si applica al <strong>Preconto / Movimento Gestionale</strong> e alle <strong>Comande</strong>.
        </p>
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <div className="text-xs font-semibold mb-3 flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-[#B45309]" /> Preconto / Movimento Gestionale</div>
            <PrintLayoutEditor
              doc="receipt"
              layout={brand.print_layout}
              onChange={(pl) => setBrand((b) => ({ ...b, print_layout: pl }))}
              testid="playout-receipt"
            />
          </div>
          <div>
            <div className="text-xs font-semibold mb-3 flex items-center gap-2"><Utensils className="w-3.5 h-3.5 text-[#1E2D5B]" /> Comande</div>
            <PrintLayoutEditor
              doc="comanda"
              layout={brand.print_layout}
              onChange={(pl) => setBrand((b) => ({ ...b, print_layout: pl }))}
              testid="playout-comanda"
            />
          </div>
        </div>
        <button
          data-testid="print-layout-save"
          onClick={saveBrand}
          disabled={brandSaving}
          className="mt-5 h-11 px-6 bg-velta-ink text-white rounded-md text-sm font-medium btn-tactile disabled:opacity-40"
        >
          {brandSaving ? "Salvataggio…" : "Salva layout di stampa"}
        </button>
      </div>

      {/* COMANDE */}
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-3 flex items-center gap-2">
          <Utensils className="w-3.5 h-3.5" /> Postazioni Comanda ({comandas.length})
        </div>
        {comandas.length === 0 ? (
          <div className="bg-velta-bg border border-dashed border-velta-border rounded-md p-6 text-center text-sm text-velta-mute">
            Nessuna postazione configurata. Aggiungine quante vuoi (Bar, Cucina, Pasticceria…) e assegna a ognuna le categorie di prodotti.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {(Array.isArray(comandas) ? comandas : []).map((p) => (
              <PrinterCard key={p.id} p={p} onEdit={() => setEditing(p)} onDelete={() => del(p.id)} {...cardProps} />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <PrinterModal
          value={editing}
          onChange={setEditing}
          onSave={() => save(editing)}
          onClose={() => setEditing(null)}
          hasFiscal={hasFiscal}
          hasPreconto={hasPrecontoPrinter}
          cats={cats}
          stores={storeList}
          items={items}
        />
      )}
    </div>
  );
}

function ConnDot({ state }) {
  const conf = {
    ok: { dot: "bg-[#2F9E77]", label: "Connessa", text: "text-[#166534]" },
    fail: { dot: "bg-red-500", label: "Non raggiungibile", text: "text-red-700" },
    testing: { dot: "bg-amber-400 animate-pulse", label: "Test in corso…", text: "text-amber-700" },
    unknown: { dot: "bg-gray-300", label: "Sconosciuto (serve QZ Tray o app Android)", text: "text-velta-mute" },
  }[state || "unknown"];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${conf.text}`}>
      <span className={`w-2.5 h-2.5 rounded-full ${conf.dot}`} />
      {conf.label}
    </span>
  );
}

function PrinterCard({ p, onEdit, onDelete, cats = [], connState = {}, onTest, qzState, storeNameOf, multi }) {
  const isFiscal = p.role === "fiscal";
  const isPreconto = p.role === "preconto";
  const info = modelInfo(p.role, p.model);
  const assignedCats = (Array.isArray(p.categories) ? p.categories : []).map((id) => cats.find((c) => c.id === id)?.name).filter(Boolean);
  const state = connState[p.id];
  return (
    <div className={`bg-white border rounded-md p-5 flex flex-col ${isFiscal ? "border-velta-ink border-2" : isPreconto ? "border-[#B45309] border-2" : "border-velta-border"}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {isFiscal ? <Printer className="w-4 h-4 text-velta-ink" /> : isPreconto ? <FileText className="w-4 h-4 text-[#B45309]" /> : <Utensils className="w-4 h-4 text-[#2F9E77]" />}
          <div>
            <div className={`text-[10px] font-mono uppercase tracking-widest ${isFiscal ? "text-velta-ink" : isPreconto ? "text-[#B45309]" : "text-[#2F9E77]"}`}>
              {isFiscal ? "Fiscale · RT" : isPreconto ? "Preconto · Non fiscale" : "Postazione Comanda"}
            </div>
            <h3 className="font-heading text-lg font-semibold mt-0.5">{p.name}</h3>
            {p.model === "simulator" && (
              <div className="text-[11px] text-amber-800 mt-1">Simulatore: non stampa carta (né A4). Eliminalo se hai la termica/RT reale.</div>
            )}
            {multi && (
              <div className="text-[10px] text-velta-mute mt-0.5">{p.store_id ? (storeNameOf?.(p.store_id) || "Sede") : "Condivisa · tutte le sedi"}</div>
            )}
          </div>
        </div>
        <button
          data-testid={`printer-delete-${p.id}`}
          onClick={onDelete}
          className="text-velta-error hover:text-velta-error/70"
          title="Elimina"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-1.5 text-sm flex-1">
        <div className="font-medium">{info.label}</div>
        <div className="text-xs text-velta-mute uppercase tracking-widest font-mono">{info.proto}</div>
        <div className="flex items-center gap-2 font-mono"><Wifi className="w-4 h-4 text-velta-subtle" /> {p.ip}:{p.port}</div>
        {isPreconto && (
          <div data-testid={`printer-gestionale-badge-${p.id}`} className={`text-xs font-medium ${p.gestionale_enabled ? "text-[#2F9E77]" : "text-velta-mute"}`}>
            {p.gestionale_enabled ? "✓ Movimenti Gestionali attivi" : "Movimenti Gestionali disattivati"}
          </div>
        )}
        {p.role === "comanda" && (
          <>
            {p.station && (
              <div className="text-xs font-mono uppercase tracking-widest text-[#2F9E77]">Reparto {p.station === "kitchen" ? "Cucina" : p.station === "pizzeria" ? "Pizzeria" : p.station === "bar" ? "Bar" : p.station}</div>
            )}
            <div className="flex flex-wrap gap-1 pt-1" data-testid={`printer-cats-${p.id}`}>
              {assignedCats.length ? (Array.isArray(assignedCats) ? assignedCats : []).map((n) => (
                <span key={n} className="px-2 py-0.5 bg-velta-bg border border-velta-border rounded text-[11px]">{n}</span>
              )) : <span className="text-[11px] text-velta-subtle italic">Nessuna categoria assegnata → riceve tutto (fallback)</span>}
            </div>
          </>
        )}
      </div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-velta-border/60">
        <div data-testid={`printer-conn-${p.id}`}><ConnDot state={state} /></div>
        <div className="flex items-center gap-3">
          <button
            data-testid={`printer-test-${p.id}`}
            onClick={() => onTest(p)}
            disabled={state === "testing"}
            className="text-xs font-medium text-velta-ink border border-velta-border hover:border-velta-ink rounded-md px-3 h-8 flex items-center gap-1.5 btn-tactile disabled:opacity-40"
            title={qzState !== "connected" && p.model !== "simulator" ? "Dal web serve QZ Tray; dall'app Android il test è nativo" : "Invia un test di connessione alla stampante"}
          >
            <Radio className="w-3.5 h-3.5" /> Testa connessione
          </button>
          <button
            data-testid={`printer-edit-${p.id}`}
            onClick={onEdit}
            className="text-sm font-medium text-[#1E2D5B] hover:underline"
          >
            Modifica
          </button>
        </div>
      </div>
    </div>
  );
}

const MODE_LABELS = {
  negozio: "Negozio", negozio_animali: "Negozio", toelettatura: "Toelettatura",
  ristorante: "Ristorante", bar: "Bar", pizzeria: "Pizzeria", gelateria: "Gelateria",
  pesati: "Prodotti a peso", rapido: "Cassa rapida", bnb: "B&B", alloggio_dedicato: "Alberghiero dedicato", festa: "Festa",
  parrucchiere: "Parrucchiere", lavanderia: "Lavanderia", autolavaggio: "Autolavaggio",
};
const prettyMode = (m) => MODE_LABELS[m] || (m ? m.replace(/_/g, " ") : "Generale");

function PrinterModal({ value, onChange, onSave, onClose, hasFiscal, hasPreconto, cats = [], stores = [], items = [] }) {
  const isFiscal = value.role === "fiscal";
  const isPreconto = value.role === "preconto";
  const isComanda = !isFiscal && !isPreconto;
  const models = MODELS_BY_ROLE[value.role] || MODELS_BY_ROLE.comanda;
  const currentModelKnown = models.some((m) => m.v === value.model);

  const setRole = (newRole) => {
    const first = MODELS_BY_ROLE[newRole][0];
    onChange({ ...value, role: newRole, model: first.v, port: first.port });
  };

  const setModel = (v) => {
    onChange({ ...value, model: v });
  };

  const toggleCat = (id) => {
    const cur = value.categories || [];
    onChange({ ...value, categories: cur.includes(id) ? (Array.isArray(cur) ? cur : []).filter((c) => c !== id) : [...cur, id] });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-xl font-semibold mb-1">
          {value.id ? "Modifica" : "Nuova"} {isFiscal ? "stampante fiscale" : isPreconto ? "stampante preconto" : "postazione comanda"}
        </h3>
        <p className="text-xs text-velta-mute mb-5">
          {isFiscal
            ? "Fiscale RT opzionale. Una per sede, oppure condivisa se non indichi la sede."
            : isPreconto
            ? "Preconto non fiscale, opzionale. Una per sede."
            : "Postazione termica per reparto. Assegna nome, IP e sede se serve."}
        </p>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Ruolo</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                data-testid="printer-role-comanda"
                onClick={() => setRole("comanda")}
                className={`h-12 rounded-md flex items-center justify-center gap-1.5 font-medium text-xs btn-tactile ${
                  isComanda ? "bg-velta-ink text-white" : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"
                }`}
              >
                <Utensils className="w-4 h-4" /> Comanda
              </button>
              <button
                type="button"
                data-testid="printer-role-preconto"
                onClick={() => setRole("preconto")}
                className={`h-12 rounded-md flex items-center justify-center gap-1.5 font-medium text-xs btn-tactile ${
                  isPreconto ? "bg-[#B45309] text-white" : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"
                }`}
              >
                <FileText className="w-4 h-4" /> Preconto
              </button>
              <button
                type="button"
                data-testid="printer-role-fiscal"
                onClick={() => setRole("fiscal")}
                className={`h-12 rounded-md flex items-center justify-center gap-1.5 font-medium text-xs btn-tactile ${
                  isFiscal ? "bg-velta-ink text-white" : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"
                }`}
              >
                <Printer className="w-4 h-4" /> Fiscale
              </button>
            </div>
          </div>

          {stores.length > 1 && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Sede (opzionale)</label>
              <select
                data-testid="printer-modal-store"
                value={value.store_id || ""}
                onChange={(e) => onChange({ ...value, store_id: e.target.value || null })}
                className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]"
              >
                <option value="">Condivisa · tutte le sedi</option>
                {(Array.isArray(stores) ? stores : []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.is_main ? " · principale" : ""}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Modello</label>
            <select
              data-testid="printer-modal-model"
              value={currentModelKnown ? value.model : ""}
              onChange={(e) => setModel(e.target.value)}
              className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]"
            >
              {!currentModelKnown && <option value="">{modelInfo(value.role, value.model).label} — scegli un modello supportato</option>}
              {(Array.isArray(models) ? models : []).map((m) => (
                <option key={m.v} value={m.v}>{m.label} — {m.proto}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">
              Nome postazione{" "}
              <span className="text-velta-subtle normal-case">
                {isFiscal ? "(es. Cassa Fiscale)" : "(es. Bar, Cucina, Pasticceria)"}
              </span>
            </label>
            <input
              data-testid="printer-modal-name"
              value={value.name}
              onChange={(e) => onChange({ ...value, name: e.target.value })}
              placeholder={isFiscal ? "Cassa Fiscale" : "Bar, Cucina, Pasticceria…"}
              className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]"
            />
          </div>

          {isComanda && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Reparto stampa</label>
              <select
                data-testid="printer-modal-station"
                value={value.station || ""}
                onChange={(e) => onChange({ ...value, station: e.target.value || null })}
                className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]"
              >
                <option value="">— Auto dal nome —</option>
                <option value="kitchen">Cucina</option>
                <option value="pizzeria">Pizzeria</option>
                <option value="bar">Bar</option>
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Indirizzo IP (fisso)</label>
              <input
                data-testid="printer-modal-ip"
                value={value.ip}
                onChange={(e) => onChange({ ...value, ip: e.target.value })}
                className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]"
              />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">
                Porta {isFiscal ? "(80 ePOS oppure 9100 TCP)" : isPreconto ? "(termica: 9100)" : ""}
              </label>
              <input
                type="number"
                min="1"
                max="65535"
                data-testid="printer-modal-port"
                value={value.port === "" || value.port == null ? "" : value.port}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") onChange({ ...value, port: "" });
                  else onChange({ ...value, port: parseInt(raw, 10) });
                }}
                onBlur={() => {
                  const n = parseInt(value.port, 10);
                  if (!Number.isFinite(n) || n < 1) {
                    onChange({ ...value, port: defaultPortFor(value.role, value.model) });
                  }
                }}
                className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]"
              />
            </div>
          </div>

          {isFiscal && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">
                Matricola RT <span className="text-velta-subtle normal-case">(es. 99MEY012345)</span>
              </label>
              <input
                data-testid="printer-modal-serial"
                value={value.fiscal_serial || ""}
                onChange={(e) => onChange({ ...value, fiscal_serial: e.target.value.toUpperCase().replace(/\s+/g, "") })}
                placeholder="99MEY012345"
                className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]"
              />
              <p className="text-[11px] text-velta-mute mt-1.5">
                Numero di matricola del registratore telematico (stampato in fondo a ogni scontrino).
                Necessaria per emettere i <strong>documenti di annullamento</strong>.
              </p>
            </div>
          )}

          {isComanda && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">
                Categorie assegnate <span className="text-velta-subtle normal-case">(es. Bevande → Bar, Dessert → Pasticceria)</span>
              </label>
              {cats.length === 0 ? (
                <div className="text-xs text-velta-mute bg-velta-bg border border-velta-border rounded-md p-3">
                  Nessuna categoria prodotti trovata. Creale in <strong>Articoli & Categorie</strong>, poi assegnale qui.
                </div>
              ) : (() => {
                const groups = cats.reduce((acc, c) => {
                  const k = c.mode || "_";
                  (acc[k] = acc[k] || []).push(c);
                  return acc;
                }, {});
                const modeKeys = Object.keys(groups);
                const showHeaders = modeKeys.length > 1;
                return (
                  <div className="border border-velta-border rounded-md p-3 max-h-52 overflow-y-auto space-y-2">
                    {(Array.isArray(modeKeys) ? modeKeys : []).map((mk) => (
                      <div key={mk}>
                        {showHeaders && (
                          <div className="text-[10px] font-mono uppercase tracking-widest text-velta-subtle mb-1">{prettyMode(mk)}</div>
                        )}
                        <div className="grid grid-cols-2 gap-1.5">
                          {(groups?.[mk] || []).map((c) => (
                            <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer py-1">
                              <input
                                type="checkbox"
                                data-testid={`printer-cat-${c.id}`}
                                checked={(value.categories || []).includes(c.id)}
                                onChange={() => toggleCat(c.id)}
                                className="w-4 h-4 accent-[#1E2D5B]"
                              />
                              <span className="truncate">{c.name}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <p className="text-[11px] text-velta-mute mt-1.5">
                I prodotti di queste categorie verranno stampati su questa postazione. Senza categorie assegnate, la postazione fa da fallback generico.
              </p>
            </div>
          )}

          {isPreconto && (
            <label className="flex items-start gap-3 bg-velta-bg border border-velta-border rounded-md p-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="printer-gestionale-flag"
                checked={!!value.gestionale_enabled}
                onChange={(e) => onChange({ ...value, gestionale_enabled: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-[#B45309]"
              />
              <span className="text-xs leading-relaxed">
                <strong>Abilita Movimenti Gestionali</strong>
                <br />
                <span className="text-velta-mute">
                  Permette di chiudere un conto come movimento <strong>NON fiscale</strong> con il gesto nascosto sulla scritta SCONTRINO in cassa
                  (pressione 2,5 s su touch, doppio click su PC). Non entra nella chiusura fiscale ma resta nelle statistiche.
                </span>
              </span>
            </label>
          )}

          {isPreconto && value.gestionale_enabled && (
            <label className="flex items-start gap-3 bg-velta-bg border border-velta-border rounded-md p-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="printer-gestionale-pin-flag"
                checked={value.gestionale_require_pin !== false}
                onChange={(e) => onChange({ ...value, gestionale_require_pin: e.target.checked })}
                className="mt-0.5 w-4 h-4 accent-[#B45309]"
              />
              <span className="text-xs leading-relaxed">
                <strong>Richiedi PIN gestionale</strong>
                <br />
                <span className="text-velta-mute">
                  Se disattivato, il Movimento Gestionale si conferma <strong>senza PIN</strong> (più veloce per consumi interni e omaggi).
                  Il PIN si imposta in Branding. Consigliato: attivo.
                </span>
              </span>
            </label>
          )}
        </div>

        <div className="flex gap-2 mt-6">
          <button
            onClick={onClose}
            className="h-12 flex-1 bg-white border border-velta-border text-velta-ink hover:border-velta-ink rounded-md font-medium btn-tactile"
          >
            Annulla
          </button>
          <button
            data-testid="printer-modal-save"
            onClick={onSave}
            className="h-12 flex-1 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile"
          >
            Salva
          </button>
        </div>
      </div>
    </div>
  );
}
